// The app's permission registry: the CRUD helper below, plus one named hook per
// confirmed non-CRUD permission ("extra"), grouped by module.
//
// Everything lives in one file on purpose. Extra slugs are backend-defined and
// must be copied from the GET /modules response, never typed from memory — a
// mistyped extra fails SILENTLY (the control simply never renders, which is
// indistinguishable from "not granted"), so the only practical defence is being
// able to read every slug the app checks side by side against that response.
//
// All permission logic — and the deliberate deny-on-absent-payload rule — stays
// in `useCan`. This file adds none of its own; it only names things.
//
// Naming convention for extras: `useCan<Verb><Subject>`, verb-first and ALWAYS
// module-qualified (useCanArchiveRestoreProject, not useCanArchiveRestore),
// since a slug like `export` or `archive` could exist on more than one module.

import useCan from './useCan';

export interface ModuleAccess {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

/**
 * The four CRUD verbs for ONE module, so a component names its slug once instead
 * of repeating it at every gate.
 *
 * Only the four core verbs, deliberately — a module's EXTRAS (any permission
 * slug outside view/create/edit/delete; see MatrixModule.extras) get their own
 * named hook below rather than a generic `can()` accessor, so every slug the app
 * checks is written down somewhere rather than assembled at a call site.
 *
 * The four calls are unconditional and fixed-order, so the rules of hooks hold;
 * each is a lookup on the memoised selectAdminPermissions map, so the object
 * rebuilt per render costs nothing worth memoising.
 *
 * @param moduleSlug the backend module slug, e.g. `projects` (see NavItem.moduleSlug).
 */
const useModuleAccess = (moduleSlug: string): ModuleAccess => ({
  canView: useCan(moduleSlug, 'view'),
  canCreate: useCan(moduleSlug, 'create'),
  canEdit: useCan(moduleSlug, 'edit'),
  canDelete: useCan(moduleSlug, 'delete'),
});

export default useModuleAccess;

// ---- extras: projects ----

/** Archive and restore a project — the `projects` module's `archive_restore`
 *  extra. One slug covers both directions. STRICT: this does NOT fall back to
 *  `edit`, so a role holding edit but not archive_restore does not get these
 *  actions. */
export const useCanArchiveRestoreProject = (): boolean => useCan('projects', 'archive_restore');

// ---- extras: people ----

/** Invite people into the company — the `people` module's `invite` extra
 *  (the "Invite by link" flow). STRICT: does NOT fall back to `create`, so a
 *  role that can add people directly cannot necessarily hand out invite links. */
export const useCanInvitePeople = (): boolean => useCan('people', 'invite');

/** Import people in bulk — the `people` module's `import` extra. STRICT: does
 *  not fall back to `create`. */
export const useCanImportPeople = (): boolean => useCan('people', 'import');

/** Export people/guest lists — the `people` module's `export` extra. STRICT:
 *  does not fall back to `view`; a role that can read the lists cannot
 *  necessarily take them out of the app. */
export const useCanExportPeople = (): boolean => useCan('people', 'export');

// ---- extras: forms_templates ----

/** Publish a form's draft design — the `forms_templates` module's `publish`
 *  extra. STRICT: does NOT fall back to `edit` — a role that can shape a form
 *  cannot necessarily put it in front of the field. Note publish presupposes a
 *  saved draft, so in practice it composes with `edit` (the builder itself is
 *  edit-gated). */
export const useCanPublishFormTemplate = (): boolean => useCan('forms_templates', 'publish');

/** Export forms — all three surfaces are live: the list Export button and the
 *  builder's Export CSV queue export-manager jobs, the builder's Export JSON
 *  is a client-side design download. The `forms_templates` module's `export`
 *  extra. STRICT: does not fall back to `view`. */
export const useCanExportFormTemplates = (): boolean => useCan('forms_templates', 'export');

/** The External-report surface (settings drawer tab, still coming-soon) — the
 *  `forms_templates` module's `external_api_export` extra. STRICT. */
export const useCanExternalApiExportFormTemplates = (): boolean =>
  useCan('forms_templates', 'external_api_export');

/** The response-set LIBRARY — the Responses modal and the in-builder picker
 *  (`forms_templates`' `responses_access` extra). One slug covers the whole
 *  surface: the backend has no create/edit/delete split for response sets.
 *  STRICT: does not fall back to `edit`. */
export const useCanAccessFormResponses = (): boolean =>
  useCan('forms_templates', 'responses_access');

// ---- extras: data_sources ----

/** Import data sources / entries from CSV — the `data_sources` module's
 *  `import` extra. Covers both the new-source upload and the entries-replace
 *  flow (and the example-template downloads inside those menus, which are
 *  useless without the right to import). STRICT: does not fall back to
 *  `create`/`edit`. */
export const useCanImportDataSources = (): boolean => useCan('data_sources', 'import');

/** Export a data source's entries (all, a selection, or one row) — the
 *  `data_sources` module's `export` extra. STRICT: does not fall back to
 *  `view`; a role that can read entries cannot necessarily take them out of
 *  the app. */
export const useCanExportDataSources = (): boolean => useCan('data_sources', 'export');

// ---- extras: reports ----

/** Download a submission's PDF (and the Export CSV stub) — the `reports`
 *  module's `export` extra. STRICT: does not fall back to `view`; a role that
 *  can read a report on screen cannot necessarily take the file out of the
 *  app. Applies wherever SubmissionsList renders — including the Inspections
 *  page's Submissions tab, which gates on `reports` by decision. */
export const useCanExportReports = (): boolean => useCan('reports', 'export');

/** Share a report (the report page's Share button, still a stub) — the
 *  `reports` module's `sharing` extra. STRICT. */
export const useCanShareReports = (): boolean => useCan('reports', 'sharing');

// ---- extras: trash ----

/** Restore trashed items back to their modules — the `trash` module's `restore`
 *  extra (gates POST trash/bulk-restore). STRICT: does NOT fall back to `edit`
 *  or `delete`; permanent deletion (trash/bulk-delete) is the module's own
 *  `delete` verb via useModuleAccess('trash'). */
export const useCanRestoreTrash = (): boolean => useCan('trash', 'restore');

// ---- extras: company_structure ----

/** Deactivate / activate / hide / unhide a structure node — the
 *  `company_structure` module's `hide_deactivate` extra. One slug covers all
 *  four transitions. STRICT: does NOT fall back to `edit`, so a role that can
 *  rename a unit cannot necessarily take it out of service. */
export const useCanHideDeactivateStructureNode = (): boolean =>
  useCan('company_structure', 'hide_deactivate');

/** Manage the structure TAG LIBRARY — create, rename and delete tags, i.e. the
 *  Tags button and its modal. Attaching an EXISTING tag to a node is ordinary
 *  `edit` (it is written by the node's own save), not this. STRICT: does not
 *  fall back to `edit`. */
export const useCanManageStructureTags = (): boolean => useCan('company_structure', 'tags');
